import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Typography from '@material-ui/core/Typography';
import { useHistory } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';

import {
  useStyles,
  listTextStyles,
  customCardStyles,
  collapseCardStyles,
} from './VehicleDetails.styled';
import { buildUrl } from 'utils';
import { Card, CollapseCard } from 'components/UI';
import { vehicleActions } from 'state/modules/vehicles';
import Hidden from '@material-ui/core/Hidden';
import moment from 'moment';
import { powertrainOptionalValues } from 'constants/vehicleTypes';
import { getPowertrains, toTitleCase } from 'utils/utils';

import i18n from 'i18n-js';

import { Link, List, ListItem, ListItemText } from '@material-ui/core';

export const VehicleTypeDetailsWidget = ({ vehicleType = {} }) => {
  const { id } = vehicleType;
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const listClasses = listTextStyles();
  const collapseCardClasses = collapseCardStyles();
  const cardClasses = customCardStyles();
  const { localeFormat } = useSelector((state) => state.organizations);

  const fetchingUploadImage = useSelector((state) => state.files?.fetchingUploadImage);
  const fetchedUploadImageSuccess = useSelector((state) => state.files?.fetchedUploadImageSuccess);
  const fetchingDeleteImage = useSelector((state) => state.files?.fetchingDeleteImage);
  const fetchedDeleteImageSuccess = useSelector((state) => state.files?.fetchedDeleteImageSuccess);

  const reload =
    (id && !fetchingUploadImage && fetchedUploadImageSuccess) ||
    (id && !fetchingDeleteImage && fetchedDeleteImageSuccess);

  useEffect(() => {
    if (reload) {
      dispatch(vehicleActions.fetchVehicleTypeById(id));
    }
  }, [reload, dispatch]);

  const goToVehicleTypeEdition = (e) => {
    e.preventDefault();
    history.push(buildUrl(`/vehicle-types/${id}/update`));
  };

  const findPowertrain = (value) => {
    const powertrain = getPowertrains()?.find((pt) => pt.value === value);
    return powertrain?.label || '';
  };

  const getVehicleTypeData = (vehicleType = {}) => {
    const vehicletypeInfo = [
      {
        title: i18n.t('vehicle_types.quick_specs'),
        items: [
          {
            label: `${i18n.t('vehicle_types.year')}:`,
            value: vehicleType?.year || '',
          },
          {
            label: `${i18n.t('vehicle_types.make')}:`,
            value: vehicleType?.make || '-',
          },
          {
            label: `${i18n.t('vehicle_types.model')}:`,
            value: vehicleType?.model || '-',
          },
          {
            label: `${i18n.t('vehicle_types.trim')}:`,
            value: vehicleType?.trim || '-',
          },
          {
            label: `${i18n.t('vehicle_types.category')}:`,
            value: toTitleCase(vehicleType?.category) || '-',
          },
        ],
      },
      {
        title: i18n.t('vehicle_types.performance_specs'),
        items: [
          {
            label: `${i18n.t('vehicle_types.powertrain')}:`,
            value: findPowertrain(vehicleType?.powertrain),
          },
          {
            label: `${i18n.t('vehicle_types.asset_class')}:`,
            value: vehicleType?.asset_class || '-',
          },
          {
            label: `${i18n.t('vehicle_types.msrp')}:`,
            value: vehicleType?.msrp || '-',
          },
          {
            label: `${i18n.t('vehicle_types.power')}:`,
            value: vehicleType?.power
              ? `${vehicleType?.power?.magnitude} ${vehicleType?.power?.units}`
              : '-',
          },
          {
            label: `${i18n.t('vehicle_types.range')}:`,
            value: vehicleType?.range
              ? `${vehicleType?.range?.magnitude} ${vehicleType?.range?.units}`
              : '-',
          },
        ],
      },
      {
        title: i18n.t('vehicle_types.engine_specs'),
        hide: !powertrainOptionalValues.includes(vehicleType?.powertrain),
        items: [
          {
            label: `${i18n.t('vehicle_types.fuel_capacity')}:`,
            value: vehicleType?.fuel_capacity
              ? `${vehicleType?.fuel_capacity?.magnitude} ${vehicleType?.fuel_capacity?.units}`
              : '-',
          },
          {
            label: `${i18n.t('vehicle_types.displacement')}:`,
            value: vehicleType?.displacement
              ? `${vehicleType?.displacement?.magnitude} ${vehicleType?.displacement?.units}`
              : '-',
          },
          {
            label: `${i18n.t('vehicle_types.cylinders')}:`,
            value: vehicleType?.cylinders || '-',
          },
          {
            label: `${i18n.t('vehicle_types.engine_type')}:`,
            value: vehicleType?.engine_type || '-',
          },
        ],
      },
    ];

    return vehicletypeInfo;
  };

  const getVehicleTypeInfo = (vehicleType = {}) => {
    const vehicletypeInfo = [{ label: 'Description:', value: vehicleType?.description || '' }];

    return vehicletypeInfo;
  };

  const Title = () => {
    return vehicleType?.year && vehicleType?.make && vehicleType?.model && vehicleType?.powertrain
      ? `${vehicleType?.year} ${vehicleType?.make} ${vehicleType?.model} ${vehicleType?.powertrain}`
      : '';
  };

  return (
    <Card title="Vehicle Type Details" headerActionType="vertical" contentRootClasses={cardClasses}>
      <>
        <div className={classes.cardContentPadding}>
          <Grid container direction="row" alignItems="center" spacing={1}>
            <Grid item xs={8}>
              <Typography variant="h3">
                <Box className={classes.name}>ID# {id}</Box>
              </Typography>
            </Grid>
            <Hidden only="xs">
              <Grid item xs={4} className={`${classes.textRight}`}>
                <Typography>
                  <Link href="#" onClick={goToVehicleTypeEdition} className={classes.vehicleLink}>
                    {i18n.t('vehicle.update_info')}
                  </Link>
                </Typography>
              </Grid>
            </Hidden>
            {vehicleType?.default_file?.thumbnail_url && (
              <Grid item xs={12}>
                <Box>
                  <img
                    alt={vehicleType.name}
                    src={vehicleType.default_file?.thumbnail_url}
                    className={classes.vehicleImg}
                  />
                </Box>
              </Grid>
            )}
            <Grid item xs={8}>
              <Typography variant="h3">
                <Box className={classes.name}>
                  <Title />
                </Box>
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <List className={classes.userList}>
                {getVehicleTypeInfo(vehicleType).map((val, index) => (
                  <ListItem key={`info-${index}`} className={classes.vehicleListItem}>
                    <ListItemText
                      classes={listClasses}
                      style={{ flexDirection: 'column' }}
                      primary={val?.label}
                      secondary={
                        <Box display="flex" component="span" flexDirection="column">
                          <Typography variant="body2" component="span" color="textSecondary">
                            {val?.value}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>

          <div>
            {getVehicleTypeData(vehicleType).map((val, index) => (
              <Box key={index}>
                {!val?.hide && (
                  <CollapseCard key={index} title={val.title} customClasses={collapseCardClasses}>
                    <List className={classes.userList}>
                      {val.items.map((val, index) => (
                        <ListItem key={index} className={classes.vehicleListItem}>
                          <ListItemText
                            classes={listClasses}
                            primary={val.label}
                            secondary={
                              <Box
                                display="flex"
                                component="span"
                                flexDirection="column"
                                className={classes.textRight}
                              >
                                <Typography
                                  variant="body2"
                                  component="span"
                                  color="textSecondary"
                                  className={classes?.[val.classes]}
                                >
                                  {val.value}
                                </Typography>
                                {val.hasOwnProperty('confirmed') &&
                                  val.confirmed !== 'Unconfirmed' && (
                                    <Typography
                                      variant="body2"
                                      component="span"
                                      className={classes.confirmed}
                                    >
                                      {i18n.t('users.confirmed')}:{' '}
                                      {moment(val.confirmed).format(`${localeFormat} hh:mm a`)}
                                    </Typography>
                                  )}
                                {val.hasOwnProperty('confirmed') &&
                                  val.confirmed === 'Unconfirmed' && (
                                    <Typography
                                      variant="body2"
                                      component="span"
                                      className={classes.unconfirmed}
                                    >
                                      {val.confirmed}
                                    </Typography>
                                  )}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CollapseCard>
                )}
              </Box>
            ))}
          </div>
        </div>
      </>
    </Card>
  );
};
